import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { LogOut, Search, Trash2, Eye, RefreshCw } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "reviewing", label: "Reviewing" },
  { value: "quoted", label: "Quoted" },
  { value: "closed", label: "Closed" },
];

const statusStyles = {
  new: "bg-accent text-accent-foreground",
  reviewing: "bg-secondary text-secondary-foreground",
  quoted: "bg-primary text-primary-foreground",
  closed: "bg-muted text-muted-foreground",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, qs] = await Promise.all([
        api.get("/admin/quotes/stats"),
        api.get("/admin/quotes", { params: { status_filter: filter, q: q || undefined } }),
      ]);
      setStats(s.data);
      setQuotes(qs.data.quotes);
    } catch (err) {
      toast.error("Failed to load");
    } finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((qq) => (qq.id === id ? data.quote : qq)));
      if (active?.id === id) setActive(data.quote);
      toast.success("Status updated");
      // refresh stats
      const s = await api.get("/admin/quotes/stats"); setStats(s.data);
    } catch {
      toast.error("Update failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((qq) => qq.id !== id));
      if (active?.id === id) setActive(null);
      setConfirmDelete(null);
      toast.success("Quote deleted");
      const s = await api.get("/admin/quotes/stats"); setStats(s.data);
    } catch { toast.error("Delete failed"); }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const statCards = useMemo(() => [
    { label: "Total Quotes", value: stats?.total ?? "—" },
    { label: "New", value: stats?.new ?? "—", accent: true },
    { label: "Reviewing", value: stats?.reviewing ?? "—" },
    { label: "Quoted", value: stats?.quoted ?? "—" },
    { label: "Closed", value: stats?.closed ?? "—" },
    { label: "New (7 days)", value: stats?.new_this_week ?? "—" },
  ], [stats]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-muted/30">
      <div className="bg-primary text-primary-foreground">
        <div className="container-x py-5 flex items-center justify-between">
          <div>
            <p className="eyebrow text-white/60">MIM Engineering</p>
            <h1 className="font-serif text-3xl md:text-4xl mt-1">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70 hidden md:inline">{user?.email}</span>
            <Button onClick={handleLogout} data-testid="admin-logout-btn" variant="ghost" className="rounded-none text-white hover:bg-white/10">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container-x py-10">
        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats">
          {statCards.map((s) => (
            <div key={s.label} className={`bg-white border border-border p-5 ${s.accent ? "border-accent" : ""}`}>
              <p className="eyebrow">{s.label}</p>
              <p className="font-serif text-4xl text-primary mt-2">{s.value}</p>
            </div>
          ))}
        </div>

        {/* FILTERS */}
        <div className="mt-8 flex flex-wrap items-end gap-3 bg-white border border-border p-4">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="admin-search"
              placeholder="Search by name, company, email, service…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 rounded-none"
            />
          </div>
          <div className="w-48">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="admin-status-filter" className="rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} data-testid="admin-refresh-btn" className="rounded-none">
            <RefreshCw size={14} className="mr-2" /> Refresh
          </Button>
        </div>

        {/* TABLE */}
        <div className="mt-6 bg-white border border-border" data-testid="admin-quotes-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name / Company</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Loading…</TableCell></TableRow>
              ) : quotes.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No quotes found.</TableCell></TableRow>
              ) : quotes.map((quote) => (
                <TableRow key={quote.id} data-testid="admin-quote-row">
                  <TableCell>
                    <div className="font-medium text-primary">{quote.name}</div>
                    {quote.company && <div className="text-xs text-muted-foreground">{quote.company}</div>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{quote.email}</TableCell>
                  <TableCell className="text-muted-foreground">{quote.service_type}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-none uppercase tracking-wider text-[10px] ${statusStyles[quote.status] || ""}`}>
                      {quote.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{new Date(quote.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid="admin-view-btn" size="sm" variant="ghost" onClick={() => setActive(quote)} className="rounded-none">
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    <Button data-testid="admin-delete-btn" size="sm" variant="ghost" onClick={() => setConfirmDelete(quote)} className="rounded-none text-destructive hover:text-destructive">
                      <Trash2 size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="admin-view-quote-dialog" className="max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-primary">{active?.name}</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4 text-sm">
              <Row label="Company" value={active.company || "—"} />
              <Row label="Email" value={active.email} />
              <Row label="Phone" value={active.phone} />
              <Row label="Service Type" value={active.service_type} />
              <Row label="Timeline" value={active.timeline || "—"} />
              <Row label="Submitted" value={new Date(active.created_at).toLocaleString()} />
              <div>
                <p className="eyebrow">Project Description</p>
                <p className="mt-2 whitespace-pre-wrap text-primary leading-relaxed">{active.project_description}</p>
              </div>
              <div className="pt-4 border-t border-border">
                <p className="eyebrow mb-2">Update Status</p>
                <Select value={active.status} onValueChange={(v) => updateStatus(active.id, v)}>
                  <SelectTrigger data-testid="admin-status-update" className="rounded-none w-60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog" className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-primary">Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the request from <strong>{confirmDelete?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm"
              className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmDelete && del(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-6 border-b border-border pb-2">
      <span className="eyebrow">{label}</span>
      <span className="text-primary text-right">{value}</span>
    </div>
  );
}
